import { FC, useState } from "react";
import { ChannelHoldout } from "@models/population.model";
import { networkLabel } from "@/shared/enums/networkColors";
import HoldoutBadge, { HoldoutStatus } from "./HoldoutBadge";
import { splitLabel } from "./TieredConversions";

/*
    Tier 2 of the Holdouts section (MPN-2077): the channel holdouts (MPN-2060), one card each.

    A channel holdout is a percentage of TREATMENT kept off one channel while still served
    everywhere else, so that channel's own lift can be measured. Tier 1 above is held out of
    everything and cannot answer that. Tier 2 draws only from the members Tier 1 left, which
    is what the connector between the two cards says.

    The channels a holdout may name, mirroring the server's HOLDOUT_CHANNELS: the bidded
    networks (EnumPublisherNetwork) and the direct-delivery channels (DeliveryChannel). The
    server validates; this list only decides what the picker offers.
*/
const HOLDOUT_CHANNELS = [
  "meta", "google", "snapchat", "pinterest", "tiktok", "linkedin", "vibe",
  "direct_mail", "email", "sms",
];

/** The splits on offer, as holdout percentages, treatment first when shown. 50 / 50 is the
 *  default: the strongest split for a two-arm comparison, and what the first channel test
 *  asked for (MPN-2060). The server takes any 1-50; a saved percentage off this list is
 *  still offered as itself, so the card shows what is actually set. */
const SPLITS = [50, 40, 30, 25, 20, 10, 5];
const DEFAULT_PCT = 50;

/** How many `pct`% of treatment is: the server's holdout_target, for the projection a
 *  not-yet-applied card shows. */
const target = (treatment: number, pct: number) =>
  treatment ? Math.max(1, Math.round((treatment * pct) / 100)) : 0;

/** What a not-yet-saved channel looks like, so a new card renders like a saved one. */
const unsaved = (channel: string): ChannelHoldout => ({
  channel,
  label: networkLabel(channel),
  enabled: false,
  pct: DEFAULT_PCT,
  treatment_count: 0,
  target_count: 0,
  held_out_count: 0,
  eligible_count: 0,
  short_by: 0,
});

const n = (value: number) => value.toLocaleString();

const ChannelTier: FC<{
  status: ChannelHoldout;
  isNew: boolean;
  /** Members Tier 1 left: what a not-yet-applied card projects its split from. */
  treatmentCount: number;
  /** Channels a new card may still name. */
  available: string[];
  saving: boolean;
  onApply: (channel: string, enabled: boolean, pct: number) => void;
}> = ({ status, isNew, treatmentCount, available, saving, onApply }) => {
  const [open, setOpen] = useState(true);
  const [channel, setChannel] = useState(status.channel);
  // A new card starts switched off: the empty card shows on every population with no channel
  // holdout, and a ticked box there read as one already applied.
  const [enabled, setEnabled] = useState(status.enabled);
  const [pct, setPct] = useState(status.pct);

  const label = isNew ? networkLabel(channel) : status.label || networkLabel(status.channel);
  const channelName = label || "the channel";
  // Applied means the policy is saved and on. It may still have held nobody out (a channel
  // already live has nobody never served on it); that is a shortfall, shown below, not a
  // holdout that has yet to be applied.
  const applied = !isNew && status.enabled;
  const locked = applied && status.published === true;
  const badge: HoldoutStatus = locked ? "locked" : applied ? "applied" : isNew ? "pending" : "off";
  const isDirty = isNew || enabled !== status.enabled || pct !== status.pct;
  // A new card applies only once ticked: saving it unticked would store a holdout that is off.
  const canApply = !locked && isDirty && !saving && channel !== "" && (!isNew || enabled);

  // Applied: the counts as they are. Otherwise the draft split projected over the members
  // Tier 1 left, and marked as a projection.
  const treatment = applied ? status.treatment_count : treatmentCount;
  const heldOut = applied ? status.held_out_count : enabled ? target(treatment, pct) : 0;
  const exposed = treatment - heldOut;
  const approx = !applied && enabled ? "~" : "";

  const splits = SPLITS.includes(pct) ? SPLITS : [...SPLITS, pct].sort((a, b) => b - a);
  const id = status.channel || "new";

  return (
    <div
      className={`holdout-tier holdout-tier--channel${locked ? " holdout-tier--locked" : ""}`}
      data-channel={channel || "new"}
      aria-disabled={locked || undefined}
    >
      <div className="holdout-tier__head">
        <button
          type="button"
          className="holdout-tier__toggle"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="population-disclosure__chevron" aria-hidden="true">{open ? "▾" : "▸"}</span>
          <span className="holdout-tier__num">Tier 2</span>
          <span className="holdout-tier__name">Channel holdout</span>
          {label && <span className="population-tag">{label}</span>}
        </button>
        {!open && (
          <span className="holdout-tier__summary">
            {splitLabel(pct)} · {approx}{n(exposed)} treatment · {approx}{n(heldOut)} holdout
          </span>
        )}
        <span className="ms-auto"><HoldoutBadge status={badge} /></span>
      </div>

      {open && (
        <div className="holdout-tier__body">
          <label className="holdout__toggle">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={saving || locked}
            />
            <span>Apply a channel holdout</span>
          </label>
          <div className="data-file__note">
            Draws only from the {n(treatmentCount)} members left after the baseline holdout.
            Channel holdout members are not shown {channelName} but receive the same mix of all
            other channels as the channel treatment group.
            {locked && " Applied and published, so it can no longer be changed."}
          </div>

          <div className="holdout-tier__controls">
            <div className="holdout-tier__field">
              <label htmlFor={`channel-holdout-channel-${id}`}>Channel</label>
              <select
                id={`channel-holdout-channel-${id}`}
                className="form-select form-select-sm"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                disabled={!isNew || saving}
                style={{ fontSize: "0.72rem" }}
              >
                {isNew && <option value="">Choose a channel</option>}
                {(isNew ? available : [status.channel]).map((c) => (
                  <option key={c} value={c}>{networkLabel(c)}</option>
                ))}
              </select>
            </div>
            <div className="holdout-tier__field">
              <label htmlFor={`channel-holdout-split-${id}`}>Split (treatment / holdout)</label>
              <select
                id={`channel-holdout-split-${id}`}
                className="form-select form-select-sm"
                value={pct}
                onChange={(e) => setPct(Number(e.target.value))}
                disabled={!enabled || saving || locked}
                style={{ fontSize: "0.72rem" }}
              >
                {splits.map((p) => (
                  <option key={p} value={p}>{splitLabel(p)}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary ms-auto"
              onClick={() => onApply(channel, enabled, pct)}
              disabled={!canApply}
              style={{ fontSize: "0.72rem" }}
            >
              {locked ? "Applied" : saving ? "Applying…" : "Apply"}
            </button>
          </div>

          <div className="holdout-arms">
            <div className="holdout-arm holdout-arm--exposed">
              <div className="holdout-arm__label">Channel treatment</div>
              <div className="holdout-arm__value">{approx}{n(exposed)}</div>
              <div className="holdout-arm__note">Shown {channelName} plus the full channel mix</div>
            </div>
            <div className="holdout-arm holdout-arm--held">
              <div className="holdout-arm__label">Channel holdout</div>
              <div className="holdout-arm__value">{approx}{n(heldOut)}</div>
              <div className="holdout-arm__note">No {channelName}; same other channels</div>
            </div>
          </div>

          {!isNew && (
            <div className="data-file__note mt-2 channel-holdout__summary">
              {status.enabled ? (
                <>
                  Held out {n(status.held_out_count)} of target {n(status.target_count)} ({status.pct}%
                  of {n(status.treatment_count)} treatment).
                  {status.short_by > 0 && (
                    <span className="population-holdout__limit channel-holdout__short">
                      {" "}Short by {n(status.short_by)}: only {n(status.eligible_count)} never
                      served on {label}.
                    </span>
                  )}
                </>
              ) : (
                <>
                  Off · the {n(status.held_out_count)} already held out are kept, and held out
                  again if this is switched back on.
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface Props {
  holdouts: ChannelHoldout[];
  /** Members Tier 1 left: the served count. */
  treatmentCount: number;
  saving: boolean;
  onApply: (channel: string, enabled: boolean, pct: number) => Promise<void>;
}

const ChannelHoldouts: FC<Props> = ({ holdouts, treatmentCount, saving, onApply }) => {
  const [adding, setAdding] = useState<string | null>(null);
  const configured = new Set(holdouts.map((h) => h.channel));
  const pending = adding && !configured.has(adding) ? adding : null;
  const available = HOLDOUT_CHANNELS.filter((c) => !configured.has(c));
  // With nothing configured, the one Tier 2 card is the empty form, channel to be chosen.
  const newCard = pending ?? (holdouts.length === 0 ? "" : null);
  // Once saved, the server lists the added channel and `pending` drops it on its own; a
  // failed save leaves the new card on screen to try again.
  const apply = (channel: string, enabled: boolean, pct: number) => void onApply(channel, enabled, pct);

  return (
    <>
      {holdouts.map((h) => (
        <ChannelTier
          // Remounts after a save so the draft starts from what the server now says.
          key={`${h.channel}|${h.enabled}|${h.pct}`}
          status={h}
          isNew={false}
          treatmentCount={treatmentCount}
          available={[]}
          saving={saving}
          onApply={apply}
        />
      ))}
      {newCard !== null && (
        <ChannelTier
          key={`new|${newCard}`}
          status={unsaved(newCard)}
          isNew
          treatmentCount={treatmentCount}
          available={available}
          saving={saving}
          onApply={apply}
        />
      )}
      {holdouts.length > 0 && pending === null && available.length > 0 && (
        <div className="holdout-tier__add">
          <select
            className="form-select form-select-sm w-auto"
            aria-label="Add channel holdout"
            value=""
            onChange={(e) => setAdding(e.target.value || null)}
            disabled={saving}
            style={{ fontSize: "0.72rem" }}
          >
            <option value="">+ Add channel holdout</option>
            {available.map((c) => (
              <option key={c} value={c}>{networkLabel(c)}</option>
            ))}
          </select>
        </div>
      )}
    </>
  );
};

export default ChannelHoldouts;
